import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import IconBankTransfer from '@/assets/icons/heroIcons/BankTransfer.svg';
import IconQRcode from '@/assets/icons/heroIcons/QRcode.svg';
import IconCardFront from '@/assets/icons/heroIcons/Card-front.svg';
import IconCollection from '@/assets/icons/heroIcons/Installments-secondary.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import MoreInfoSection from '@/sections/MoreInfo';
import { Button } from '@/components/buttons';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { Text } from '@/components/typography';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React, { PropsWithChildren, ReactNode } from 'react';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import HeroSimple from '@/sections/HeroSimple';
import { SeoHeader } from '@/components/SeoHeader';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
interface ItemBoxProps {
  icon: ReactNode;
  title: string;
  delay: string;
}
function ItemBox(t0) {
  const $ = _c(13);
  const {
    icon,
    delay,
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <span>{title}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon || $[3] !== t1) {
    t2 = <h3 className="items-s flex justify-start gap-4 text-lg font-bold lg:text-3xl">{icon}{t1}</h3>;
    $[2] = icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== delay) {
    t3 = <p className="text-light-gray-500">{delay}</p>;
    $[5] = delay;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="mt-4 flex flex-col gap-4 text-sm lg:mt-5 lg:gap-5 lg:text-base">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="h-full rounded-2xl bg-white px-8 py-8 md:rounded-4xl md:py-10">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function ListItem(t0) {
  const $ = _c(7);
  const {
    index,
    children
  } = t0;
  let t1;
  if ($[0] !== index) {
    t1 = <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-800 text-xs font-bold text-white lg:h-6 lg:w-6 lg:text-sm">{index}</div>;
    $[0] = index;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <p>{children}</p>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-row gap-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(t0) {
  const $ = _c(159);
  const {
    menuPromoBanners
  } = t0;
  usePageView("how-to-pay");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["howToPay", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("seo.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("seo.description");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("banner.title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link to={LINKS().login} testId="howToPayLogin" analyticsParam="howToPayLogin" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Link to={LINKS().mobileApp} testId="howToPayMobileApp" analyticsParam="howToPayMobileApp" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = <Text variant="p1" className="mx-auto max-w-192 text-center text-gray-800"><Trans t={t} i18nKey="banner.text">{t6}{t7}</Trans></Text>;
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t8) {
    t9 = <HeroSimple title={t5} titleClassName="mb-4 lg:mb-8" circleClassName="bgCircle absolute right-0 top-0 left-auto hidden h-[50rem] w-[50rem] -translate-y-1/4! translate-x-1/3! rotate-180 lg:block" className="bg-blue-50" withoutCharacters={true}>{t8}</HeroSimple>;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconBankTransfer className="h-7 w-7 text-blue-500 lg:h-10 lg:w-10" />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t) {
    t11 = t("types.transfer.title");
    $[18] = t;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("types.transfer.delay");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Strong />;
    t14 = <br />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t) {
    t15 = <p><Trans t={t} i18nKey="types.transfer.text.1">{t13}{t14}</Trans></p>;
    $[24] = t;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Strong />;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Link to={LINKS().login} testId="howToPayLogin" analyticsParam="howToPayLogin" />;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t) {
    t18 = <p><Trans t={t} i18nKey="types.transfer.text.2">{t16}{t17}</Trans></p>;
    $[28] = t;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t15 || $[33] !== t18) {
    t19 = <ItemBox icon={t10} title={t11} delay={t12}>{t15}{t18}</ItemBox>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t15;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconQRcode className="h-7 w-7 text-blue-500 lg:h-10 lg:w-10" />;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t) {
    t21 = t("types.qr.title");
    $[36] = t;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t) {
    t22 = t("types.qr.delay");
    $[38] = t;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t) {
    t23 = t("types.qr.text.1");
    $[40] = t;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t23) {
    t24 = <p>{t23}</p>;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t) {
    t25 = t("types.qr.text.2");
    $[44] = t;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t25) {
    t26 = <h4 className="mb-2 text-base font-semibold lg:text-xl">{t25}</h4>;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t) {
    t27 = t("types.qr.text.3");
    $[48] = t;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t27) {
    t28 = <ListItem index={1}>{t27}</ListItem>;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t) {
    t29 = t("types.qr.text.4");
    $[52] = t;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t29) {
    t30 = <ListItem index={2}>{t29}</ListItem>;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Link to={LINKS().login} testId="howToPayLogin" analyticsParam="howToPayLogin" />;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t) {
    t32 = <ListItem index={3}><Trans t={t} i18nKey="types.qr.text.5">{t31}</Trans></ListItem>;
    $[57] = t;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== t) {
    t33 = t("types.qr.text.6");
    $[59] = t;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t33) {
    t34 = <ListItem index={4}>{t33}</ListItem>;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t26 || $[64] !== t28 || $[65] !== t30 || $[66] !== t32 || $[67] !== t34) {
    t35 = <div className="flex flex-col gap-2">{t26}{t28}{t30}{t32}{t34}</div>;
    $[63] = t26;
    $[64] = t28;
    $[65] = t30;
    $[66] = t32;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t21 || $[70] !== t22 || $[71] !== t24 || $[72] !== t35) {
    t36 = <ItemBox icon={t20} title={t21} delay={t22}>{t24}{t35}</ItemBox>;
    $[69] = t21;
    $[70] = t22;
    $[71] = t24;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <IconCollection className="h-7 w-7 text-blue-500 lg:h-10 lg:w-10" />;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t) {
    t38 = t("types.collection.title");
    $[75] = t;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== t) {
    t39 = t("types.collection.delay");
    $[77] = t;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  let t40;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      login_link: <Link to={LINKS().login} testId="howToPayLogin" analyticsParam="howToPayLogin" />
    };
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== t) {
    t41 = <p><Trans t={t} i18nKey="types.collection.text.1" components={t40} /></p>;
    $[80] = t;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  let t42;
  if ($[82] !== t) {
    t42 = t("types.collection.text.2");
    $[82] = t;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== t42) {
    t43 = <h4 className="text-base font-semibold lg:text-xl">{t42}</h4>;
    $[84] = t42;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      strong: <Strong />,
      br: <br />
    };
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  let t46;
  if ($[87] !== t) {
    t45 = <p><Trans t={t} i18nKey="types.collection.text.3" components={t44} /></p>;
    t46 = t("types.collection.text.4");
    $[87] = t;
    $[88] = t45;
    $[89] = t46;
  } else {
    t45 = $[88];
    t46 = $[89];
  }
  let t47;
  if ($[90] !== t46) {
    t47 = <p>{t46}</p>;
    $[90] = t46;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== t38 || $[93] !== t39 || $[94] !== t41 || $[95] !== t43 || $[96] !== t45 || $[97] !== t47) {
    t48 = <ItemBox icon={t37} title={t38} delay={t39}>{t41}{t43}{t45}{t47}</ItemBox>;
    $[92] = t38;
    $[93] = t39;
    $[94] = t41;
    $[95] = t43;
    $[96] = t45;
    $[97] = t47;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  let t49;
  if ($[99] !== t19 || $[100] !== t36 || $[101] !== t48) {
    t49 = <div className="bg-blue-50 px-4 pt-4 pb-20 lg:pt-10 lg:pb-40"><div className="mx-auto flex max-w-192 flex-col gap-6">{t19}{t36}{t48}</div></div>;
    $[99] = t19;
    $[100] = t36;
    $[101] = t48;
    $[102] = t49;
  } else {
    t49 = $[102];
  }
  let t50;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[103] = t50;
  } else {
    t50 = $[103];
  }
  let t51;
  if ($[104] !== t) {
    t51 = t("selfCare.title");
    $[104] = t;
    $[105] = t51;
  } else {
    t51 = $[105];
  }
  let t52;
  if ($[106] !== t51) {
    t52 = <SectionHeadline className="mx-auto mb-4 max-w-192 lg:mb-6" anchorKey="selfCare.title">{t51}</SectionHeadline>;
    $[106] = t51;
    $[107] = t52;
  } else {
    t52 = $[107];
  }
  let t53;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <Link to={LINKS().faq} testId="howToPayFaq" analyticsParam="howToPayFaq" />;
    $[108] = t53;
  } else {
    t53 = $[108];
  }
  let t54;
  if ($[109] !== t) {
    t54 = <Text variant="p2" className="text-center text-gray-800"><Trans t={t} i18nKey="selfCare.text">{t53}</Trans></Text>;
    $[109] = t;
    $[110] = t54;
  } else {
    t54 = $[110];
  }
  let t55;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = LINKS();
    $[111] = t55;
  } else {
    t55 = $[111];
  }
  const t56 = t55.login;
  let t57;
  if ($[112] !== t) {
    t57 = t("selfCare.cta");
    $[112] = t;
    $[113] = t57;
  } else {
    t57 = $[113];
  }
  let t58;
  if ($[114] !== t57) {
    t58 = <Button to={t56} testId="howToPayLogin" analyticsParam="howToPayLogin" size="lg" variant="primary" align="center" className="mt-10 md:mt-12">{t57}</Button>;
    $[114] = t57;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] !== t52 || $[117] !== t54 || $[118] !== t58) {
    t59 = <HeroSimple circleClassName="hidden" contentClassName="py-10 lg:py-20">{t52}{t54}{t58}</HeroSimple>;
    $[116] = t52;
    $[117] = t54;
    $[118] = t58;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <IconDeferredPayment className="fillButtonIcon" />;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== t) {
    t61 = t("core:products.deferredPayment");
    $[121] = t;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = LINKS();
    $[123] = t62;
  } else {
    t62 = $[123];
  }
  let t63;
  if ($[124] !== t61) {
    t63 = {
      analyticsParam: "howToPayMoreInfoDeferredPayment",
      icon: t60,
      testId: "howToPayMoreInfoeDeferredPayment",
      title: t61,
      to: t62.deferredPayment
    };
    $[124] = t61;
    $[125] = t63;
  } else {
    t63 = $[125];
  }
  let t64;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <IconMaxi className="fillButtonIcon" />;
    $[126] = t64;
  } else {
    t64 = $[126];
  }
  let t65;
  if ($[127] !== t) {
    t65 = t("core:products.revolving");
    $[127] = t;
    $[128] = t65;
  } else {
    t65 = $[128];
  }
  let t66;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = LINKS();
    $[129] = t66;
  } else {
    t66 = $[129];
  }
  let t67;
  if ($[130] !== t65) {
    t67 = {
      analyticsParam: "howToPayMoreInfoRevolvingApplication",
      icon: t64,
      testId: "howToPayMoreInfoRevolvingApplication",
      title: t65,
      to: t66.revolving
    };
    $[130] = t65;
    $[131] = t67;
  } else {
    t67 = $[131];
  }
  let t68;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <IconThird className="fillButtonIcon" />;
    $[132] = t68;
  } else {
    t68 = $[132];
  }
  let t69;
  if ($[133] !== t) {
    t69 = t("core:link.payInThree");
    $[133] = t;
    $[134] = t69;
  } else {
    t69 = $[134];
  }
  let t70;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = LINKS();
    $[135] = t70;
  } else {
    t70 = $[135];
  }
  let t71;
  if ($[136] !== t69) {
    t71 = {
      analyticsParam: "howToPayMoreInfoPayInThree",
      icon: t68,
      testId: "howToPayMoreInfoPayInThree",
      title: t69,
      to: t70.payInThree
    };
    $[136] = t69;
    $[137] = t71;
  } else {
    t71 = $[137];
  }
  let t72;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <IconCardFront className="fillButtonIcon" />;
    $[138] = t72;
  } else {
    t72 = $[138];
  }
  let t73;
  if ($[139] !== t) {
    t73 = t("core:products.card");
    $[139] = t;
    $[140] = t73;
  } else {
    t73 = $[140];
  }
  let t74;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = LINKS();
    $[141] = t74;
  } else {
    t74 = $[141];
  }
  let t75;
  if ($[142] !== t73) {
    t75 = {
      analyticsParam: "howToPayMoreInfoCard",
      icon: t72,
      testId: "howToPayMoreInfoCard",
      title: t73,
      to: t74.card
    };
    $[142] = t73;
    $[143] = t75;
  } else {
    t75 = $[143];
  }
  let t76;
  if ($[144] !== t63 || $[145] !== t67 || $[146] !== t71 || $[147] !== t75) {
    t76 = <div className="relative z-10"><MoreInfoSection buttons={[t63, t67, t71, t75]} /></div>;
    $[144] = t63;
    $[145] = t67;
    $[146] = t71;
    $[147] = t75;
    $[148] = t76;
  } else {
    t76 = $[148];
  }
  let t77;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[149] = t77;
  } else {
    t77 = $[149];
  }
  let t78;
  if ($[150] !== t59 || $[151] !== t76) {
    t78 = <div className="relative overflow-hidden bg-blue-100">{t59}{t76}{t77}</div>;
    $[150] = t59;
    $[151] = t76;
    $[152] = t78;
  } else {
    t78 = $[152];
  }
  let t79;
  if ($[153] !== menuPromoBanners || $[154] !== t4 || $[155] !== t49 || $[156] !== t78 || $[157] !== t9) {
    t79 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t9}{t49}{t50}{t78}</Layout>;
    $[153] = menuPromoBanners;
    $[154] = t4;
    $[155] = t49;
    $[156] = t78;
    $[157] = t9;
    $[158] = t79;
  } else {
    t79 = $[158];
  }
  return t79;
}
export const getStaticProps: GetStaticProps = async ctx => {
  const isAllowed = isAllowedRoute('jak-zaplatit');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'howToPay']))
    }
  };
};
const Strong = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <strong className="font-semibold">{children}</strong>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};